import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { type CustomSale, saveCustomSales } from "./analytics.customSales";
import {
  parseCsv,
  guessColumns,
  buildImportRows,
  buildImportConflicts,
  buildMergedSales,
  type ParsedImportRow,
  type ImportRowError,
  type ImportMode,
} from "./analytics.csvImport.logic";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  SaveErrorText,
  EmptyNote,
  FormField,
  FormLabel,
  FormRow,
  ScrollArea,
  DropZone,
  DropZoneHint,
  HiddenFileInput,
  FileRow,
  FileName,
  ChangeFileBtn,
  ColumnSelect,
  ModeToggleRow,
  ModeBtn,
  ModeHint,
  SummaryRow,
  SummaryBadge,
  PreviewTableWrapper,
  PreviewTableScroll,
  PreviewTable,
  PreviewTableHead,
  PreviewTableRow,
  PreviewRowError,
  WarningBanner,
  WarningBannerTitle,
  WarningList,
} from "./analytics.csvImport.styles";

interface CsvImportPaneProps {
  open: boolean;
  companyId: number;
  /** Full, unfiltered custom sales — the base the import appends onto, and
   * what same-date conflicts are checked against. */
  existing: CustomSale[];
  onSave: (sales: CustomSale[]) => void;
  onClose: () => void;
}

const formatIsoDate = (iso: string): string => {
  const d = LocalDate.parse(iso);
  return `${String(d.dayOfMonth()).padStart(2, "0")}/${String(
    d.monthValue(),
  ).padStart(2, "0")}/${d.year()}`;
};

const ROW_ERROR_KEYS: Record<ImportRowError, string> = {
  "invalid-date": "CompanyAnalytics.ImportRowErrorInvalidDate",
  "invalid-amount": "CompanyAnalytics.ImportRowErrorInvalidAmount",
  "not-positive": "CompanyAnalytics.ImportRowErrorNotPositive",
};

export const CsvImportPane = (props: CsvImportPaneProps) => {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[] | null>(null);
  const [dateColumnIndex, setDateColumnIndex] = useState(0);
  const [amountColumnIndex, setAmountColumnIndex] = useState(1);
  const [importMode, setImportMode] = useState<ImportMode>("add");
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setFileName(null);
      setRawRows([]);
      setHeaders(null);
      setDateColumnIndex(0);
      setAmountColumnIndex(1);
      setImportMode("add");
      setIsDragging(false);
      setSaveError(false);
    }
    wasOpen.current = props.open;
  }, [props.open]);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const parsed = parseCsv(text);
      const guess = guessColumns(parsed.rows);
      setFileName(file.name);
      setRawRows(parsed.rows);
      setHeaders(parsed.headers);
      setDateColumnIndex(guess.dateColumnIndex);
      setAmountColumnIndex(guess.amountColumnIndex);
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so choosing the same file again still fires onChange.
    e.target.value = "";
  };

  const columnCount = useMemo(
    () =>
      rawRows.reduce((max, r) => Math.max(max, r.length), headers?.length ?? 0),
    [rawRows, headers],
  );

  const previewRows: ParsedImportRow[] = useMemo(
    () => buildImportRows(rawRows, dateColumnIndex, amountColumnIndex),
    [rawRows, dateColumnIndex, amountColumnIndex],
  );

  const validRows = previewRows.filter((r) => !r.error);
  const errorRows = previewRows.filter((r) => r.error);

  const conflicts = useMemo(
    () => buildImportConflicts(previewRows, props.existing),
    [previewRows, props.existing],
  );

  const columnLabel = (index: number) =>
    headers?.[index] ??
    t("CompanyAnalytics.ImportColumnPlaceholder", { number: index + 1 });

  const handleSave = () => {
    if (validRows.length === 0) return;
    const merged = buildMergedSales(importMode, props.existing, previewRows);
    setIsSaving(true);
    setSaveError(false);
    saveCustomSales(props.companyId, merged)
      .then(() => {
        setIsSaving(false);
        props.onSave(merged);
      })
      .catch(() => {
        setIsSaving(false);
        setSaveError(true);
      });
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.ImportSalesTitle")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.ImportSalesDescription")}
        </PaneDescription>

        <ScrollArea>
          {!fileName ? (
            <DropZone
              $active={isDragging}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) handleFile(file);
              }}
            >
              <SecondaryButton
                label={t("CompanyAnalytics.ChooseFile")}
                onClick={() => fileInputRef.current?.click()}
              />
              <DropZoneHint>{t("CompanyAnalytics.DropCsvHere")}</DropZoneHint>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInputChange}
              />
            </DropZone>
          ) : (
            <>
              <FileRow>
                <FileName>{fileName}</FileName>
                <ChangeFileBtn
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("CompanyAnalytics.ChangeFile")}
                </ChangeFileBtn>
              </FileRow>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInputChange}
              />

              <FormRow>
                <FormField>
                  <FormLabel>{t("CompanyAnalytics.DateColumn")}</FormLabel>
                  <ColumnSelect
                    value={dateColumnIndex}
                    onChange={(e) => setDateColumnIndex(Number(e.target.value))}
                  >
                    {Array.from({ length: columnCount }, (_, i) => (
                      <option key={i} value={i}>
                        {columnLabel(i)}
                      </option>
                    ))}
                  </ColumnSelect>
                </FormField>
                <FormField>
                  <FormLabel>{t("CompanyAnalytics.AmountColumn")}</FormLabel>
                  <ColumnSelect
                    value={amountColumnIndex}
                    onChange={(e) =>
                      setAmountColumnIndex(Number(e.target.value))
                    }
                  >
                    {Array.from({ length: columnCount }, (_, i) => (
                      <option key={i} value={i}>
                        {columnLabel(i)}
                      </option>
                    ))}
                  </ColumnSelect>
                </FormField>
              </FormRow>

              <FormField>
                <FormLabel>{t("CompanyAnalytics.ImportModeLabel")}</FormLabel>
                <ModeToggleRow>
                  <ModeBtn
                    type="button"
                    $active={importMode === "add"}
                    onClick={() => setImportMode("add")}
                  >
                    {t("CompanyAnalytics.ImportModeAdd")}
                  </ModeBtn>
                  <ModeBtn
                    type="button"
                    $active={importMode === "overwrite"}
                    onClick={() => setImportMode("overwrite")}
                  >
                    {t("CompanyAnalytics.ImportModeOverwrite")}
                  </ModeBtn>
                </ModeToggleRow>
                <ModeHint>
                  {t(
                    importMode === "add"
                      ? "CompanyAnalytics.ImportModeHintAdd"
                      : "CompanyAnalytics.ImportModeHintOverwrite",
                  )}
                </ModeHint>
              </FormField>

              {previewRows.length === 0 ? (
                <EmptyNote>{t("CompanyAnalytics.ImportNoValidRows")}</EmptyNote>
              ) : (
                <>
                  <SummaryRow>
                    <SummaryBadge $tone="positive">
                      {t("CompanyAnalytics.ImportValidRows", {
                        count: validRows.length,
                      })}
                    </SummaryBadge>
                    {errorRows.length > 0 && (
                      <SummaryBadge $tone="negative">
                        {t("CompanyAnalytics.ImportErrorRows", {
                          count: errorRows.length,
                        })}
                      </SummaryBadge>
                    )}
                  </SummaryRow>

                  {/* What this shows depends on the mode chosen above: for
                      "add" it's purely informational (the total after
                      combining); for "overwrite" it's a heads-up that the
                      existing amount is about to be discarded. */}
                  {conflicts.length > 0 && (
                    <WarningBanner>
                      <WarningBannerTitle>
                        {t(
                          importMode === "add"
                            ? "CompanyAnalytics.ImportConflictWarningAdd"
                            : "CompanyAnalytics.ImportConflictWarningOverwrite",
                          { count: conflicts.length },
                        )}
                      </WarningBannerTitle>
                      <WarningList>
                        {conflicts.map((c) => (
                          <li key={c.date}>
                            {importMode === "add"
                              ? t("CompanyAnalytics.ImportConflictLineAdd", {
                                  date: formatIsoDate(c.date),
                                  existing: Math.round(
                                    c.existingAmount,
                                  ).toLocaleString("sv-SE"),
                                  imported: Math.round(
                                    c.importedAmount,
                                  ).toLocaleString("sv-SE"),
                                  total: Math.round(
                                    c.existingAmount + c.importedAmount,
                                  ).toLocaleString("sv-SE"),
                                })
                              : t(
                                  "CompanyAnalytics.ImportConflictLineOverwrite",
                                  {
                                    date: formatIsoDate(c.date),
                                    existing: Math.round(
                                      c.existingAmount,
                                    ).toLocaleString("sv-SE"),
                                    imported: Math.round(
                                      c.importedAmount,
                                    ).toLocaleString("sv-SE"),
                                  },
                                )}
                          </li>
                        ))}
                      </WarningList>
                    </WarningBanner>
                  )}

                  <PreviewTableWrapper>
                    <PreviewTableScroll>
                      <PreviewTable>
                        <PreviewTableHead>
                          <tr>
                            <th>{t("CompanyAnalytics.SaleDate")}</th>
                            <th>{t("CompanyAnalytics.CostAmount")}</th>
                            <th />
                          </tr>
                        </PreviewTableHead>
                        <tbody>
                          {previewRows.map((row) => (
                            <PreviewTableRow
                              key={row.rowNumber}
                              $hasError={!!row.error}
                            >
                              <td>
                                {row.date
                                  ? formatIsoDate(row.date.toString())
                                  : row.rawDate}
                              </td>
                              <td>
                                {row.amount !== null
                                  ? `${Math.round(row.amount).toLocaleString("sv-SE")} kr`
                                  : row.rawAmount}
                              </td>
                              <td>
                                {row.error && (
                                  <PreviewRowError>
                                    {t(ROW_ERROR_KEYS[row.error])}
                                  </PreviewRowError>
                                )}
                              </td>
                            </PreviewTableRow>
                          ))}
                        </tbody>
                      </PreviewTable>
                    </PreviewTableScroll>
                  </PreviewTableWrapper>
                </>
              )}
            </>
          )}
        </ScrollArea>

        <PaneFooter>
          {saveError && (
            <SaveErrorText>{t("CompanyAnalytics.SaveError")}</SaveErrorText>
          )}
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
            disabled={isSaving}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.ImportButton", {
              count: validRows.length,
            })}
            onClick={handleSave}
            disabled={isSaving || validRows.length === 0}
            isLoading={isSaving}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
